"use client";

import React, { useState, useEffect } from "react";
import { Award, Clock, ShieldCheck } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  id: "quality",
  title: "Maximale Transparenz",
  description: "Jede Spende wird direkt auf der Blockchain verzeichnet. Stiftungen und Donoren können den Geldfluss in Echtzeit nachverfolgen – manipulationssicher und transparent nach Schweizer Standards.",
  icon: ShieldCheck,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1657173/img/swiss_blockchain_transparency.png"
}, {
  id: "delivery",
  title: "Sofortige Abwicklung",
  description: "Vergessen Sie tagelange Banklaufzeiten. Crypto-Spenden werden nahezu in Echtzeit abgewickelt, sodass Ihre Hilfe genau dann ankommt, wenn sie am dringendsten benötigt wird.",
  icon: Clock,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1657173/img/instant_crypto_settlement.png"
}, {
  id: "trust",
  title: "Schweizer Präzision",
  description: "Wir kombinieren modernste Fintech-Innovation mit der Sicherheit des Schweizer Finanzplatzes. Ein vertrauenswürdiges Ökosystem für Philanthropie im digitalen Zeitalter.",
  icon: Award,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1657173/img/swiss_crypto_excellence.png"
}];
const AUTO_CYCLE_DURATION = 5000;
const UPDATE_INTERVAL = 50;
export default function WhyChooseVickyTraders() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    if (isPaused) return;
    const increment = UPDATE_INTERVAL / AUTO_CYCLE_DURATION * 100;
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          setActiveIndex(current => (current + 1) % features.length);
          return 0;
        }
        return prev + increment;
      });
    }, UPDATE_INTERVAL);
    return () => clearInterval(interval);
  }, [isPaused]);
  const handleTabClick = (index: number) => {
    setActiveIndex(index);
    setProgress(0);
  };
  return <section data-section-id="222900" id="warum-crypto4good" className="bg-background text-foreground py-20 lg:py-32 overflow-hidden" ref={ref}>
      <div className={`container mx-auto px-4 md:px-6 transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-24">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight mb-6 text-foreground">
            Warum Crypto4Good?
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Wir schlagen die Brücke zwischen digitalem Wohlstand und gemeinnützigem Engagement. Erleben Sie Philanthropie mit Schweizer Sicherheit und technologischer Exzellenz.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {}
          <div className="lg:col-span-5 flex flex-col gap-4">
            {features.map((feature, i) => {
            const isActive = activeIndex === i;
            const Icon = feature.icon;
            return <button key={feature.id} data-index={i} onClick={() => handleTabClick(i)} onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} className={`relative text-left p-6 md:p-8 rounded-2xl transition-all duration-300 border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${isActive ? "bg-card border-border shadow-xl scale-[1.02]" : "bg-transparent border-transparent hover:bg-accent/50 hover:border-border/50"}`} aria-expanded={isActive}>
                  <div className="flex items-start gap-5">
                    <div className={`p-3 rounded-lg shrink-0 transition-colors duration-300 ${isActive ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="space-y-3 flex-1">
                      <h3 className={`text-xl font-default font-semibold transition-colors duration-300 ${isActive ? "text-primary" : "text-foreground"}`}>
                        {feature.title}
                      </h3>
                      
                      <div className={`grid transition-all duration-500 ease-in-out ${isActive ? "grid-rows-[1fr] opacity-100 mt-3" : "grid-rows-[0fr] opacity-0"}`}>
                        <div className="overflow-hidden">
                          <p className="text-muted-foreground font-default leading-relaxed text-sm md:text-base pb-6">
                            {feature.description}
                          </p>
                          <Progress value={isActive ? progress : 0} className="h-1.5 bg-secondary" />
                        </div>
                      </div>
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {}
          <div className="lg:col-span-7 relative h-[400px] md:h-[500px] lg:h-[650px] rounded-3xl overflow-hidden bg-card border border-border shadow-2xl">
            {features.map((feature, i) => {
            const isActive = activeIndex === i;
            return <div key={`image-${feature.id}`} data-index={i} className={`absolute inset-0 transition-all duration-1000 ease-in-out ${isActive ? "opacity-100 scale-100 z-10" : "opacity-0 scale-105 z-0"}`}>
                  <img src={feature.image} alt={feature.title} className="w-full h-full object-cover" />
                  {}
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                  
                  {}
                  <div className="absolute bottom-8 left-8 right-8">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 backdrop-blur-md border border-border shadow-md">
                      <feature.icon className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium font-default text-primary">
                        {feature.title}
                      </span>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}